import { useState, useEffect } from 'react';
import { useSiteOffice } from '@/contexts/SiteOfficeContext';
import { createCustomSiteOffice, updateCustomSiteOffice, deleteCustomSiteOffice } from '@/services/siteOfficeService';
import { useToast } from "@/hooks/use-toast";

export const useSiteOfficeSelection = (onSiteOfficeSelected: (siteOffice: string) => void) => {
  const { 
    siteOffices, 
    accessibleSiteOffices, 
    loading, 
    refreshSiteOffices,
    canAccessAllSiteOffices,
    userSiteOfficeId
  } = useSiteOffice();
  const { toast } = useToast();
  const [selectedOffice, setSelectedOffice] = useState<string>('');
  const [customOffice, setCustomOffice] = useState<string>('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);

  // Auto-select if user only has access to one site office
  useEffect(() => {
    if (!canAccessAllSiteOffices && accessibleSiteOffices.length === 1) {
      const singleOffice = accessibleSiteOffices[0];
      setSelectedOffice(singleOffice.name);
      console.log('Auto-selected single accessible office:', singleOffice.name);
    }
  }, [canAccessAllSiteOffices, accessibleSiteOffices]);

  const handleOfficeSelect = (office: string) => {
    setSelectedOffice(office);
    setShowCustomInput(false);
    setCustomOffice('');
    console.log('Selected office:', office);
  };

  const handleCustomSelect = () => {
    setSelectedOffice('custom');
    setShowCustomInput(true);
    console.log('Custom office selected');
  };

  const handleCustomOfficeChange = (value: string) => {
    setCustomOffice(value);
    console.log('Custom office name changed:', value);
  };

  const handleEditCustomOffice = async (officeId: string, newName: string) => {
    console.log('🎯 handleEditCustomOffice called with:', { officeId, newName });
    console.log('🔍 Available site offices:', siteOffices.map(o => ({ id: o.id, name: o.name })));
    
    try {
      console.log('🔄 Calling updateCustomSiteOffice service...');
      const updatedOffice = await updateCustomSiteOffice(officeId, newName);
      console.log('✅ Site office updated successfully:', updatedOffice);
      
      console.log('🔄 Refreshing site offices list...');
      await refreshSiteOffices();
      console.log('✅ Site offices list refreshed');
      
      // Update selection if the edited office was selected
      if (selectedOffice === siteOffices.find(o => o.id === officeId)?.name) {
        console.log('🔄 Updating selected office name to:', newName);
        setSelectedOffice(newName);
      }
      
      toast({
        title: "✅ Site Office Updated",
        description: `Site office has been updated to "${newName}".`,
      });
    } catch (error) {
      console.error('❌ Error editing custom site office:', error);
      toast({
        title: "❌ Error",
        description: error instanceof Error ? error.message : "Failed to update site office.",
        variant: "destructive",
      });
      throw error; // Re-throw to let the component handle loading states
    }
  };

  const handleDeleteCustomOffice = async (officeId: string) => {
    console.log('🎯 handleDeleteCustomOffice called with:', officeId);
    console.log('🔍 Available site offices:', siteOffices.map(o => ({ id: o.id, name: o.name })));
    
    const officeToDelete = siteOffices.find(office => office.id === officeId);
    console.log('🗑️ Office to delete:', officeToDelete);
    
    try {
      console.log('🔄 Calling deleteCustomSiteOffice service...');
      const result = await deleteCustomSiteOffice(officeId);
      console.log('✅ Site office deleted successfully:', result);
      
      // Clear selection if the deleted office was selected
      if (officeToDelete && selectedOffice === officeToDelete.name) {
        console.log('🔄 Clearing selected office as it was deleted');
        setSelectedOffice('');
      }
      
      console.log('🔄 Refreshing site offices list...');
      await refreshSiteOffices();
      console.log('✅ Site offices list refreshed');
      
      toast({
        title: "✅ Site Office Deleted",
        description: `"${officeToDelete?.name || 'Site office'}" has been deleted successfully.`,
      });
    } catch (error) {
      console.error('❌ Error deleting custom site office:', error);
      toast({
        title: "❌ Error",
        description: error instanceof Error ? error.message : "Failed to delete site office.",
        variant: "destructive",
      });
      throw error; // Re-throw to let the component handle loading states
    }
  };

  const handleProceed = async () => {
    console.log('handleProceed called with:', { selectedOffice, customOffice });
    
    try {
      let finalOffice = selectedOffice;
      let siteOfficeData = null;
      
      if (selectedOffice === 'custom' && customOffice.trim()) {
        console.log('Creating custom site office:', customOffice.trim());
        setIsCreatingCustom(true);
        
        try {
          siteOfficeData = await createCustomSiteOffice(customOffice.trim());
          console.log('Custom site office created:', siteOfficeData);
          
          await refreshSiteOffices();
          finalOffice = customOffice.trim();
          
          toast({
            title: "Site Office Created Successfully",
            description: `Custom site office "${customOffice.trim()}" has been created successfully.`,
          });
        } catch (error) {
          console.error('Error creating custom site office:', error);
          toast({
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to create custom site office. Please try again.",
            variant: "destructive",
          });
          return;
        } finally {
          setIsCreatingCustom(false);
        }
      } else if (selectedOffice !== 'custom') {
        // Find the existing site office data
        siteOfficeData = siteOffices.find(office => office.name === selectedOffice);
        console.log('Found existing site office data:', siteOfficeData);
      }
      
      if (finalOffice && finalOffice !== 'custom' && siteOfficeData) {
        // Store both the name and UUID for data filtering
        localStorage.setItem('selectedSiteOffice', finalOffice);
        localStorage.setItem('selectedSiteOfficeId', siteOfficeData.id);
        localStorage.setItem('selectedSiteOfficeUuid', siteOfficeData.id);
        
        console.log(`✅ Selected Site Office: ${finalOffice} (UUID: ${siteOfficeData.id})`);
        
        toast({
          title: "Site Office Selected",
          description: `You have selected "${finalOffice}".`,
        });
        
        onSiteOfficeSelected(finalOffice);
      } else {
        console.error('Invalid selection or missing site office data');
        toast({
          title: "Error",
          description: "Please select a valid site office or provide a custom name.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error in handleProceed:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const isValid = Boolean(selectedOffice && (selectedOffice !== 'custom' || (customOffice.trim().length >= 3)));

  return {
    siteOffices: accessibleSiteOffices, // Return only accessible offices
    allSiteOffices: siteOffices, // Keep all offices available if needed
    loading,
    selectedOffice,
    customOffice,
    showCustomInput,
    isCreatingCustom,
    isValid,
    canAccessAllSiteOffices,
    handleOfficeSelect,
    handleCustomSelect,
    handleCustomOfficeChange,
    handleEditCustomOffice,
    handleDeleteCustomOffice,
    handleProceed
  };
};
